import React, { useState } from 'react';
import {
  Steps,
  Upload,
  Select,
  Button,
  DatePicker,
  InputNumber,
  Typography,
  Collapse,
  message,
  Alert,
  Card,
} from 'antd';
import { InboxOutlined, FileSearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { toMonthDayjs } from '../utils/monthDayjs';
import api from '../api';
import PayrollInspectionCompareView from './PayrollInspectionCompareView';
import './PayrollInspection.css';

const { Dragger } = Upload;
const { Text } = Typography;

export default function PayrollInspectionWizard({ onComplete }) {
  const [step, setStep] = useState(0);
  const [fileList, setFileList] = useState([]);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [sheetName, setSheetName] = useState(null);
  const [headerRowIndex, setHeaderRowIndex] = useState(0);
  const [monthKey, setMonthKey] = useState(() => dayjs().startOf('month').format('YYYY-MM'));
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [resolutions, setResolutions] = useState({});

  const uploadFile = async () => {
    const file = fileList[0]?.originFileObj || fileList[0];
    if (!file) {
      message.warning('Please select an Excel file (.xlsx)');
      return;
    }
    setLoading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/admin/payroll-inspection/parse', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!res.data?.success) throw new Error(res.data?.message || 'Parse failed');
      setUploadedFileName(file.name || 'uploaded.xlsx');
      setSessionId(res.data.sessionId);
      setSheets(res.data.sheets || []);
      if (res.data.sheets?.[0]) {
        setSheetName(res.data.sheets[0].name);
        setHeaderRowIndex(res.data.sheets[0].headerRowIndex ?? 0);
      }
      setStep(1);
      message.success('File loaded — select worksheet');
    } catch (e) {
      message.error(e.response?.data?.message || e.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const runAnalyze = async (monthOverride, { keepStep = false } = {}) => {
    if (!sessionId || !sheetName) {
      message.warning('Select a worksheet');
      return;
    }
    const monthArg = monthOverride && (typeof monthOverride === 'string' || typeof monthOverride?.isValid === 'function')
      ? monthOverride
      : null;
    const mk = toMonthDayjs(monthArg || monthKey);
    if (!mk) {
      message.warning('Please select payroll month');
      return;
    }
    const mkStr = mk.format('YYYY-MM');
    setMonthKey(mkStr);
    setLoading(true);
    try {
      const res = await api.post('/admin/payroll-inspection/analyze', {
        sessionId,
        sheetName,
        monthKey: mkStr,
        headerRowIndex,
      });
      if (!res.data?.success) throw new Error(res.data?.message || 'Analysis failed');
      setReport(res.data.report);
      const initial = {};
      (res.data.report?.rows || []).forEach((row) => {
        row.differences.forEach((d) => {
          initial[d.id] = 'excel';
        });
      });
      setResolutions(initial);
      if (!keepStep) setStep(2);
      else message.success(`Compared with Thinktech data for ${mk.format('MMMM YYYY')}`);
    } catch (e) {
      message.error(e.response?.data?.message || e.message || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const buildFixes = (rowsSubset) => {
    const fixes = [];
    rowsSubset.forEach((row) => {
      row.differences.forEach((d) => {
        const resolution = resolutions[d.id] || 'excel';
        fixes.push({
          resolution,
          field: d.field,
          staffUserId: d.staffUserId,
          excelValue: d.excelValue,
          advanceId: d.advanceIds?.[0],
        });
      });
    });
    return fixes;
  };

  const applyFixes = async (fixes) => {
    if (!fixes.length) {
      message.info('No differences to apply');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/admin/payroll-inspection/apply-fixes', { fixes });
      const applied = (res.data?.results || []).filter((r) => r.applied).length;
      message.success(`Processed ${fixes.length} item(s). Software updated: ${applied}.`);
      onComplete?.();
    } catch (e) {
      message.error(e.response?.data?.message || 'Could not apply fixes');
    } finally {
      setLoading(false);
    }
  };

  const onApplyStaff = (row) => applyFixes(buildFixes([row]));

  const onApplyAll = () => {
    const withDiffs = (report?.rows || []).filter((r) => r.differences?.length > 0);
    applyFixes(buildFixes(withDiffs));
  };

  if (step === 2 && report) {
    return (
      <PayrollInspectionCompareView
        report={report}
        monthKey={monthKey}
        fileName={uploadedFileName}
        resolutions={resolutions}
        setResolutions={setResolutions}
        loading={loading}
        onApplyStaff={onApplyStaff}
        onApplyAll={onApplyAll}
        onBack={() => setStep(1)}
        onMonthChange={(d) => runAnalyze(d, { keepStep: true })}
      />
    );
  }

  return (
    <Card className="pi-page">
      <div style={{ marginBottom: 8 }}>
        <Text type="secondary">Client Excel vs Thinktech database reconciliation</Text>
      </div>
      <Steps
        size="small"
        current={step}
        style={{ marginBottom: 20 }}
        items={[
          { title: 'Upload Excel' },
          { title: 'Worksheet' },
          { title: 'Compare & fix' },
        ]}
      />

      {step === 0 && (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Upload your payroll Excel. We match staff by ID or name and compare absent, OT, advances, and net salary side by side."
          />
          <Dragger
            accept=".xlsx,.xls"
            maxCount={1}
            fileList={fileList}
            beforeUpload={() => false}
            onChange={({ fileList: fl }) => setFileList(fl)}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Click or drag Excel file here</p>
          </Dragger>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
            <Button type="primary" loading={loading} onClick={uploadFile} disabled={!fileList.length}>
              Next
            </Button>
          </div>
        </>
      )}

      {step === 1 && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <Text strong>Worksheet</Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                value={sheetName}
                onChange={(v) => {
                  setSheetName(v);
                  const sh = sheets.find((s) => s.name === v);
                  if (sh) setHeaderRowIndex(sh.headerRowIndex ?? 0);
                }}
                options={sheets.map((s) => ({ label: `${s.name} (${s.rowCount} rows)`, value: s.name }))}
              />
            </div>
            <div>
              <Text strong>Payroll month (Thinktech DB)</Text>
              <div style={{ fontSize: 12, color: '#727786', marginTop: 4, marginBottom: 8 }}>
                Attendance, payroll, and advances are compared for this month (pick the correct month if your Excel is for a different period).
              </div>
              <DatePicker
                picker="month"
                style={{ width: '100%' }}
                value={toMonthDayjs(monthKey)}
                onChange={(d) => {
                  const next = toMonthDayjs(d);
                  if (next) setMonthKey(next.format('YYYY-MM'));
                }}
                format="MMMM YYYY"
                placeholder="Select month"
                allowClear={false}
              />
            </div>
            <div>
              <Text strong>Header row (0-based)</Text>
              <InputNumber
                min={0}
                style={{ width: '100%', marginTop: 8 }}
                value={headerRowIndex}
                onChange={(v) => setHeaderRowIndex(v ?? 0)}
              />
            </div>
            {sheets.find((s) => s.name === sheetName)?.previewRows?.length > 0 && (
              <Collapse
                items={[{
                  key: 'preview',
                  label: 'Preview JSON (first rows)',
                  children: (
                    <pre style={{ maxHeight: 200, overflow: 'auto', fontSize: 11 }}>
                      {JSON.stringify(sheets.find((s) => s.name === sheetName)?.previewRows, null, 2)}
                    </pre>
                  ),
                }]}
              />
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
            <Button onClick={() => setStep(0)}>Back</Button>
            <Button type="primary" loading={loading} icon={<FileSearchOutlined />} onClick={() => runAnalyze()}>
              Run inspection
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
